import type {
  CashMovementInput,
  Page,
  PosCashMovement,
  PosOwnDrawerInput,
  PosReturn,
  PosReturnInput,
  PosReturnListItem,
  PosSale,
  PosSaleInput,
  PosSaleListItem,
  PosShift,
  PosShiftReport,
  PosTerminal,
  PosTerminalInput,
  PosTerminalUpdate,
  PosUnlinkedReturnInput,
  ShiftCloseInput,
  ShiftOpenInput,
  ShiftSummary,
  ShiftSummaryQuery,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '../../lib/api'
import { invalidateLedger } from '../accounting/queries'
import { financeKeys } from '../finance/queries'
import { invalidateSales } from '../sales/queries'
import { tillLoyaltyKeys } from './loyalty'

export interface ShiftListParams {
  page: number
  pageSize: number
  status?: 'OPEN' | 'CLOSED' | undefined
  terminalId?: string | undefined
}

export interface SaleListParams {
  page: number
  pageSize: number
  shiftId?: string | undefined
  q?: string | undefined
  /** Exactly this invoice number, case-insensitive (the till return lookup). */
  invoiceNumber?: string | undefined
}

/** Every point-of-sale query lives under ['pos']: anything done at the till refreshes them together. */
export const posKeys = {
  all: ['pos'] as const,
  terminals: ['pos', 'terminals'] as const,
  terminal: (id: string) => ['pos', 'terminals', 'detail', id] as const,
  shifts: ['pos', 'shifts'] as const,
  currentShifts: ['pos', 'shifts', 'current'] as const,
  shiftList: (query: ShiftListParams) => ['pos', 'shifts', 'list', query] as const,
  shiftSummary: (query: ShiftSummaryQuery) => ['pos', 'shifts', 'summary', query] as const,
  shift: (id: string) => ['pos', 'shifts', 'detail', id] as const,
  report: (id: string) => ['pos', 'shifts', 'report', id] as const,
  sales: ['pos', 'sales'] as const,
  saleList: (query: SaleListParams) => ['pos', 'sales', 'list', query] as const,
  sale: (id: string) => ['pos', 'sales', 'detail', id] as const,
  returns: ['pos', 'returns'] as const,
  returnList: (query: ReturnListParams) => ['pos', 'returns', 'list', query] as const,
  return: (id: string) => ['pos', 'returns', 'detail', id] as const,
}

export interface ReturnListParams {
  shiftId: string
  page: number
  pageSize: number
}

const path = (base: string, id: string, action?: string) =>
  `${base}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

// ── tills ────────────────────────────────────────────────────────────────────

export const terminalsQueryOptions = queryOptions({
  queryKey: posKeys.terminals,
  queryFn: ({ signal }) => api.get<PosTerminal[]>('/pos/terminals', { signal }),
})

export function terminalQueryOptions(id: string) {
  return queryOptions({
    queryKey: posKeys.terminal(id),
    queryFn: ({ signal }) => api.get<PosTerminal>(path('/pos/terminals', id), { signal }),
  })
}

export function useSaveTerminal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: PosTerminalInput | PosTerminalUpdate }) =>
      id
        ? api.put<PosTerminal>(path('/pos/terminals', id), input)
        : api.post<PosTerminal>('/pos/terminals', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: posKeys.terminals }),
  })
}

/**
 * "Give this till its own drawer" (BR-POS-004): a new cash account, the till pointed at it and the cash in
 * the drawer transferred to it. The accounts, their pickers and the transfers change too.
 */
export function useOwnDrawer(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PosOwnDrawerInput) =>
      api.post<PosTerminal>(path('/pos/terminals', id, 'own-drawer'), input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: posKeys.terminals }),
        queryClient.invalidateQueries({ queryKey: financeKeys.treasury }),
        queryClient.invalidateQueries({ queryKey: financeKeys.transfers }),
        invalidateLedger(queryClient),
      ]),
  })
}

// ── shifts ───────────────────────────────────────────────────────────────────

/** The caller's open shifts (at most one per till). */
export const currentShiftsQueryOptions = queryOptions({
  queryKey: posKeys.currentShifts,
  queryFn: ({ signal }) => api.get<PosShift[]>('/pos/shifts/current', { signal }),
})

export function shiftListQueryOptions(query: ShiftListParams) {
  return queryOptions({
    queryKey: posKeys.shiftList(query),
    queryFn: ({ signal }) =>
      api.get<Page<PosShift>>('/pos/shifts', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          status: query.status,
          terminalId: query.terminalId,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/**
 * Counts per status, the open floats and six months of net till sales and drawer differences
 * (GET /pos/shifts/summary), over the shifts the caller may list; follows the till filter.
 */
export function shiftSummaryQueryOptions(query: ShiftSummaryQuery) {
  return queryOptions({
    queryKey: posKeys.shiftSummary(query),
    queryFn: ({ signal }) =>
      api.get<ShiftSummary>('/pos/shifts/summary', { signal, query: { terminalId: query.terminalId } }),
    placeholderData: keepPreviousData,
  })
}

export function shiftQueryOptions(id: string) {
  return queryOptions({
    queryKey: posKeys.shift(id),
    queryFn: ({ signal }) => api.get<PosShift>(path('/pos/shifts', id), { signal }),
  })
}

/** X report (open shift, live) or Z report (closed shift, frozen at close). */
export function shiftReportQueryOptions(id: string) {
  return queryOptions({
    queryKey: posKeys.report(id),
    queryFn: ({ signal }) => api.get<PosShiftReport>(path('/pos/shifts', id, 'report'), { signal }),
  })
}

/** Anything done on a shift changes its figures, its sales and — once posted — the books and stock. */
export function invalidatePos(queryClient: QueryClient, options: { ledger?: boolean } = {}) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: posKeys.shifts }),
    queryClient.invalidateQueries({ queryKey: posKeys.sales }),
    queryClient.invalidateQueries({ queryKey: posKeys.returns }),
    queryClient.invalidateQueries({ queryKey: posKeys.terminals }),
    options.ledger ? invalidateSales(queryClient, { ledger: true }) : undefined,
  ])
}

/**
 * `onOpened` runs before the refresh: the refreshed current shifts replace the screen that opened
 * the shift, and a callback given to `mutate` would be dropped with it.
 */
export function useOpenShift(onOpened?: (shift: PosShift) => void) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ShiftOpenInput) => api.post<PosShift>('/pos/shifts', input),
    onSuccess: (shift) => {
      onOpened?.(shift)
      return invalidatePos(queryClient)
    },
  })
}

/** `onClosed` runs before the refresh, for the same reason: the closed shift leaves the till. */
export function useCloseShift(shiftId: string, onClosed?: (report: PosShiftReport) => void) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ShiftCloseInput) =>
      api.post<PosShiftReport>(path('/pos/shifts', shiftId, 'close'), input),
    onSuccess: (report) => {
      queryClient.setQueryData(posKeys.report(shiftId), report)
      queryClient.setQueryData(posKeys.shift(shiftId), report.shift)
      onClosed?.(report)
      // The over/short posts to the ledger.
      return Promise.all([invalidatePos(queryClient), invalidateLedger(queryClient)])
    },
  })
}

export function useCashMovement(shiftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CashMovementInput) =>
      api.post<PosCashMovement>(path('/pos/shifts', shiftId, 'cash-movements'), input),
    onSuccess: () =>
      Promise.all([
        invalidatePos(queryClient),
        invalidateLedger(queryClient),
        queryClient.invalidateQueries({ queryKey: financeKeys.transfers }),
      ]),
  })
}

// ── sales ────────────────────────────────────────────────────────────────────

export function saleListQueryOptions(query: SaleListParams) {
  return queryOptions({
    queryKey: posKeys.saleList(query),
    queryFn: ({ signal }) =>
      api.get<Page<PosSaleListItem>>('/pos/sales', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          shiftId: query.shiftId,
          q: query.q,
          invoiceNumber: query.invoiceNumber,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

export function saleQueryOptions(id: string) {
  return queryOptions({
    queryKey: posKeys.sale(id),
    queryFn: ({ signal }) => api.get<PosSale>(path('/pos/sales', id), { signal }),
  })
}

export function useSell() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PosSaleInput) => api.post<PosSale>('/pos/sales', input),
    onSuccess: (sale) => {
      queryClient.setQueryData(posKeys.sale(sale.id), sale)
      return Promise.all([
        invalidatePos(queryClient, { ledger: true }),
        // A named customer's points moved (earned, redeemed): the till's and the loyalty pages' views.
        // The till's are only marked stale: read again when the customer is next picked, not while the
        // finished cart (and its points) is still on screen.
        sale.loyalty
          ? queryClient.invalidateQueries({ queryKey: tillLoyaltyKeys.customers, refetchType: 'none' })
          : undefined,
        sale.loyalty ? queryClient.invalidateQueries({ queryKey: ['loyalty'] }) : undefined,
      ])
    },
  })
}

export function useVoidSale(saleId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) => api.post<PosSale>(path('/pos/sales', saleId, 'void'), { reason }),
    onSuccess: (sale) => {
      queryClient.setQueryData(posKeys.sale(sale.id), sale)
      return invalidatePos(queryClient, { ledger: true })
    },
  })
}

// ── returns ──────────────────────────────────────────────────────────────────

/** A shift's returns, newest first (GET /pos/returns?shiftId=). */
export function returnListQueryOptions(query: ReturnListParams) {
  return queryOptions({
    queryKey: posKeys.returnList(query),
    queryFn: ({ signal }) =>
      api.get<Page<PosReturnListItem>>('/pos/returns', {
        signal,
        query: { shiftId: query.shiftId, page: query.page, pageSize: query.pageSize },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Everything a return's 80 mm receipt prints. */
export function returnQueryOptions(id: string) {
  return queryOptions({
    queryKey: posKeys.return(id),
    queryFn: ({ signal }) => api.get<PosReturn>(path('/pos/returns', id), { signal }),
  })
}

export function useCreateReturn() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PosReturnInput) => api.post<PosReturn>('/pos/returns', input),
    onSuccess: (ret) => {
      queryClient.setQueryData(posKeys.return(ret.id), ret)
      return invalidatePos(queryClient, { ledger: true })
    },
  })
}

/** A return with no till sale behind it (pos.return_unlinked, BR-POS-015). */
export function useCreateUnlinkedReturn() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PosUnlinkedReturnInput) => api.post<PosReturn>('/pos/returns/unlinked', input),
    onSuccess: (ret) => {
      queryClient.setQueryData(posKeys.return(ret.id), ret)
      return invalidatePos(queryClient, { ledger: true })
    },
  })
}
